import { useState } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { getApiErrorMessage, isApiError } from "~/utils/apiClient";

interface GiveUpOwnershipDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agentName: string;
  // Throws on failure; the rejection message is shown inline. The backend
  // returns a 409 with a reason when the caller is the agent's only owner.
  onConfirm: () => Promise<void>;
}

export const GiveUpOwnershipDialog = ({
  open,
  onOpenChange,
  agentName,
  onConfirm,
}: GiveUpOwnershipDialogProps) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setError(null);
    }
    onOpenChange(next);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (e) {
      setError(
        isApiError(e) ? getApiErrorMessage(e) : "Failed to give up ownership. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Give up ownership of &ldquo;{agentName}&rdquo;?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="flex flex-col gap-1">
              <span>
                You&rsquo;ll lose access to this agent unless it&rsquo;s shared with you again.
              </span>
              {error && <span className="text-destructive">{error}</span>}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
          <Button variant="destructive" onClick={handleConfirm} disabled={submitting}>
            {submitting ? "Giving up…" : "Give up"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
